import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { hasFullAccess } from '../lib/platform';
import UpgradeScreen from '../components/UpgradeScreen';
import Logo from '../components/Logo';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Search, 
  Bot, 
  MapPin, 
  Ruler, 
  Loader2,
  Building2,
  ChevronLeft,
  History,
  Sparkles,
  Target,
  BarChart3,
  CheckCircle2,
  Clock,
  Map,
  FileText,
  Layers,
  Info,
  ExternalLink,
  X,
  Calendar,
  Hash,
  Square,
  Euro,
  Droplets,
  AlertTriangle,
  Factory,
  Shield,
  ShieldAlert,
  ShieldCheck,
  CircleAlert,
  Home,
  TrendingUp,
  Landmark,
  Ban,
  Check,
  HelpCircle,
  Building,
  TreePine
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

// Leaflet imports
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const CadastreAgentPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isClubMember = hasFullAccess(user?.subscription_tier);

  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [results, setResults] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [mapCenter, setMapCenter] = useState([46.603354, 1.888334]);
  const [mapZoom, setMapZoom] = useState(6);
  const [visibleLayers, setVisibleLayers] = useState({
    parcelles: true,
    batiments: true,
    sections: false
  });
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [parcelTransactions, setParcelTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [parcelRisques, setParcelRisques] = useState(null);
  const [loadingRisques, setLoadingRisques] = useState(false);
  const [parcelUrbanisme, setParcelUrbanisme] = useState(null);
  const [loadingUrbanisme, setLoadingUrbanisme] = useState(false);
  const [parcelEstimation, setParcelEstimation] = useState(null);
  const [loadingEstimation, setLoadingEstimation] = useState(false);
  const [parcelBatiment, setParcelBatiment] = useState(null);
  const [loadingBatiment, setLoadingBatiment] = useState(false);
  const [criteria, setCriteria] = useState({
    localisation: '',
    surface_min: '',
    type_parcelle: 'tous'
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCommune, setSelectedCommune] = useState(null);

  // Loading steps
  const loadingSteps = [
    { label: 'Recherche', description: 'Localisation de la commune...' },
    { label: 'Collecte', description: 'Récupération des parcelles cadastrales...' },
    { label: 'Analyse IA', description: 'Analyse des données cadastrales...' },
    { label: 'Finalisation', description: 'Préparation de la carte...' }
  ];

  const loadingTips = [
    "Les parcelles cadastrales sont mises à jour annuellement",
    "La surface cadastrale peut différer de la surface habitable",
    "Vérifiez toujours le PLU avant d'acheter un terrain",
    "Les servitudes peuvent impacter la valeur d'une parcelle",
    "Le cadastre est consultable gratuitement en mairie",
    "Une parcelle peut avoir plusieurs propriétaires (indivision)"
  ];

  // Simulate loading progress
  useEffect(() => {
    let stepInterval;
    let messageInterval;
    
    if (loading) {
      stepInterval = setInterval(() => {
        setLoadingStep(prev => {
          if (prev < loadingSteps.length - 1) return prev + 1;
          return prev;
        });
      }, 3000);

      let tipIndex = 0;
      setLoadingMessage(loadingTips[0]);
      messageInterval = setInterval(() => {
        tipIndex = (tipIndex + 1) % loadingTips.length;
        setLoadingMessage(loadingTips[tipIndex]);
      }, 5000);
    } else {
      setLoadingStep(0);
      setLoadingMessage('');
    }

    return () => {
      clearInterval(stepInterval);
      clearInterval(messageInterval);
    };
  }, [loading]);

  if (!isClubMember) {
    return (
      <UpgradeScreen
        icon={MapPin}
        iconBgClass="from-blue-500 to-cyan-600"
        iconColorClass="text-blue-500"
        buttonGradientClass="from-blue-500 to-cyan-600"
        title="Agent IA Cadastre & DVF"
        currentTier={user?.subscription_tier}
        features={[
          { icon: Search, title: "Recherche cadastrale", description: "Accédez aux données cadastrales de toute la France" },
          { icon: BarChart3, title: "Données DVF", description: "Consultez les transactions immobilières récentes" },
          { icon: Map, title: "Cartographie interactive", description: "Visualisez les parcelles et bâtiments sur carte" }
        ]}
      />
    );
  }

  // Search addresses
  const searchAddresses = async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=8`);
      const features = response.data.features || [];
      setSuggestions(features.map(f => ({
        label: f.properties.label,
        name: f.properties.name,
        city: f.properties.city,
        postcode: f.properties.postcode,
        citycode: f.properties.citycode,
        context: f.properties.context,
        coordinates: f.geometry.coordinates,
        type: f.properties.type
      })));
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error searching addresses:', error);
    }
  };

  const handleSelectAddress = (address) => {
    setSelectedCommune({
      code: address.citycode,
      nom: address.city,
      label: address.label,
      coordinates: address.coordinates
    });
    setCriteria({ ...criteria, localisation: address.label });
    setShowSuggestions(false);
    
    // Centrer la carte sur l'adresse
    if (address.coordinates) {
      setMapCenter([address.coordinates[1], address.coordinates[0]]);
      setMapZoom(17);
    }
  };

  // Ouvrir le détail d'une parcelle
  const handleParcelClick = async (properties, geometry) => {
    setSelectedParcel({ properties, geometry });
    setParcelTransactions([]);
    setParcelRisques(null);
    setParcelUrbanisme(null);
    setParcelEstimation(null);
    setParcelBatiment(null);
    setLoadingTransactions(true);
    setLoadingRisques(true);
    setLoadingUrbanisme(true);
    setLoadingEstimation(true);
    setLoadingBatiment(true);
    
    // Calculer le centroïde de la parcelle pour les requêtes de risques
    let parcelCenter = null;
    if (geometry && geometry.coordinates) {
      try {
        // Pour un MultiPolygon ou Polygon, prendre le premier point comme approximation
        const coords = geometry.type === 'MultiPolygon' 
          ? geometry.coordinates[0][0] 
          : geometry.coordinates[0];
        if (coords && coords.length > 0) {
          // Calculer le centroïde simplifié (moyenne des coordonnées)
          const sumLon = coords.reduce((acc, c) => acc + c[0], 0);
          const sumLat = coords.reduce((acc, c) => acc + c[1], 0);
          parcelCenter = {
            lon: sumLon / coords.length,
            lat: sumLat / coords.length
          };
        }
      } catch (e) {
        console.error('Error calculating parcel center:', e);
      }
    }
    
    const codeInsee = properties.commune || properties.code_insee || selectedCommune?.code;
    const surface = properties.contenance || 0;
    const parcelleId = properties.id || properties.idu || '';
    
    // Lancer les requêtes en parallèle
    const fetchTransactions = async () => {
      try {
        if (codeInsee) {
          const response = await axios.get(`${API}/dvf/transactions`, {
            params: {
              code_commune: codeInsee,
              annee_min: 2015,
              annee_max: 2025,
              limit: 50
            }
          });
          
          const section = properties.section;
          const transactions = response.data.transactions || [];
          
          const relevantTx = transactions.filter(tx => {
            const txSection = tx.adresse?.includes(section);
            return txSection || transactions.length < 10;
          }).slice(0, 5);
          
          setParcelTransactions(relevantTx.length > 0 ? relevantTx : transactions.slice(0, 5));
        }
      } catch (error) {
        console.error('Error fetching parcel transactions:', error);
      }
      setLoadingTransactions(false);
    };
    
    const fetchRisques = async () => {
      try {
        if (parcelCenter) {
          const response = await axios.get(`${API}/dvf/risques`, {
            params: {
              lat: parcelCenter.lat,
              lon: parcelCenter.lon,
              code_insee: codeInsee
            }
          });
          setParcelRisques(response.data);
        }
      } catch (error) {
        console.error('Error fetching parcel risks:', error);
        setParcelRisques({ error: true, message: 'Données de risques non disponibles' });
      }
      setLoadingRisques(false);
    };
    
    const fetchUrbanisme = async () => {
      try {
        if (parcelCenter) {
          const response = await axios.get(`${API}/dvf/urbanisme`, {
            params: {
              lat: parcelCenter.lat,
              lon: parcelCenter.lon
            }
          });
          setParcelUrbanisme(response.data);
        }
      } catch (error) {
        console.error('Error fetching urbanisme:', error);
        setParcelUrbanisme({ error: true, message: 'Données PLU non disponibles' });
      }
      setLoadingUrbanisme(false);
    };
    
    const fetchEstimation = async () => {
      try {
        if (codeInsee && surface > 0) {
          const response = await axios.get(`${API}/dvf/estimation-prix`, {
            params: {
              code_insee: codeInsee,
              surface: surface,
              type_bien: 'terrain'
            }
          });
          setParcelEstimation(response.data);
        }
      } catch (error) {
        console.error('Error fetching estimation:', error);
        setParcelEstimation({ estimation_disponible: false });
      }
      setLoadingEstimation(false);
    };
    
    const fetchBatiment = async () => {
      try {
        if (codeInsee && parcelleId) {
          const response = await axios.get(`${API}/dvf/parcelle-batiment`, {
            params: {
              code_insee: codeInsee,
              parcelle_id: parcelleId,
              lat: parcelCenter?.lat,
              lon: parcelCenter?.lon
            }
          });
          setParcelBatiment(response.data);
        }
      } catch (error) {
        console.error('Error fetching batiment:', error);
        setParcelBatiment({ has_batiment: null });
      }
      setLoadingBatiment(false);
    };
    
    // Exécuter en parallèle
    await Promise.all([
      fetchTransactions(), 
      fetchRisques(),
      fetchUrbanisme(),
      fetchEstimation(),
      fetchBatiment()
    ]);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);
  };

  const formatSurface = (contenance) => {
    if (!contenance) return 'N/A';
    const m2 = contenance;
    if (m2 >= 10000) {
      return `${(m2 / 10000).toFixed(2)} ha`;
    }
    return `${m2.toLocaleString()} m²`;
  };

  const handleSearch = async () => {
    if (!selectedCommune) {
      toast.error('Veuillez sélectionner une adresse');
      return;
    }

    setLoading(true);
    setResults(null);
    setLoadingStep(0);

    try {
      // Étape 1: Recherche
      await new Promise(resolve => setTimeout(resolve, 1500));
      setLoadingStep(1);

      // Étape 2: Collecte de toutes les couches cadastrales en parallèle
      const [parcellesResponse, batimentsResponse, sectionsResponse] = await Promise.all([
        axios.get(`${API}/dvf/parcelles?code_insee=${selectedCommune.code}&layer=parcelles`),
        axios.get(`${API}/dvf/parcelles?code_insee=${selectedCommune.code}&layer=batiments`),
        axios.get(`${API}/dvf/parcelles?code_insee=${selectedCommune.code}&layer=sections`)
      ]);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLoadingStep(2);

      // Étape 3: Analyse IA (appel au backend pour générer l'analyse)
      const analyseResponse = await axios.post(`${API}/cadastre/analyse`, {
        code_insee: selectedCommune.code,
        commune_nom: selectedCommune.nom,
        adresse: selectedCommune.label,
        parcelles_count: parcellesResponse.data.total,
        batiments_count: batimentsResponse.data.total,
        sections_count: sectionsResponse.data.total,
        surface_min: criteria.surface_min ? parseInt(criteria.surface_min) : null,
        type_parcelle: criteria.type_parcelle
      }, { withCredentials: true });
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLoadingStep(3);

      // Étape 4: Finalisation
      setResults({
        parcelles: parcellesResponse.data,
        batiments: batimentsResponse.data,
        sections: sectionsResponse.data,
        analyse: analyseResponse.data,
        commune: selectedCommune
      });

      // Ajouter à l'historique
      setSearchHistory(prev => [{
        adresse: selectedCommune.label,
        commune: selectedCommune.nom,
        code: selectedCommune.code,
        date: new Date().toISOString(),
        parcelles_count: parcellesResponse.data.total,
        batiments_count: batimentsResponse.data.total
      }, ...prev.slice(0, 4)]);

      const totalElements = parcellesResponse.data.total + batimentsResponse.data.total;
      toast.success(`${totalElements.toLocaleString()} éléments cadastraux trouvés et analysés`);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  // Map component to fit bounds around the address
  const FitBounds = ({ parcelles, addressCoords }) => {
    const map = useMap();
    useEffect(() => {
      // Si on a les coordonnées de l'adresse, centrer dessus d'abord
      if (addressCoords) {
        map.setView([addressCoords[1], addressCoords[0]], 17);
      } else if (parcelles?.features?.length > 0) {
        // Sinon, ajuster aux parcelles
        const geojsonLayer = L.geoJSON(parcelles);
        const bounds = geojsonLayer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [20, 20] });
        }
      }
    }, [parcelles, addressCoords, map]);
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00B050] to-[#0D47A1] flex items-center justify-center shadow-lg">
              <Map className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Agent IA Cadastre</h1>
              <p className="text-slate-500">Recherche et analyse des parcelles cadastrales</p>
            </div>
          </div>
          {searchHistory.length > 0 && (
            <Button variant="outline" className="gap-2">
              <History className="h-4 w-4" />
              Historique ({searchHistory.length})
            </Button>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Search Panel */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg border-0 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Search className="h-5 w-5 text-[#00B050]" />
                  Critères de recherche
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 pt-4">
                {/* Location */}
                <div className="space-y-2 relative">
                  <Label className="flex items-center gap-2 text-slate-300">
                    <MapPin className="h-4 w-4 text-[#00B050]" />
                    Adresse ou lieu
                  </Label>
                  <Input
                    data-testid="cadastre-location"
                    placeholder="15 rue de la Paix, Paris..."
                    value={criteria.localisation}
                    onChange={(e) => {
                      setCriteria({ ...criteria, localisation: e.target.value });
                      searchAddresses(e.target.value);
                    }}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border max-h-60 overflow-auto">
                      {suggestions.map((address, idx) => (
                        <button
                          key={idx}
                          data-testid={`suggestion-${idx}`}
                          className="w-full px-4 py-3 text-left hover:bg-slate-50 flex flex-col text-slate-900 border-b border-slate-100 last:border-0"
                          onClick={() => handleSelectAddress(address)}
                        >
                          <span className="font-medium text-sm">{address.label}</span>
                          <span className="text-xs text-slate-500">{address.context}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Surface minimum */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-slate-300">
                    <Ruler className="h-4 w-4 text-[#00B050]" />
                    Surface minimum (m²)
                  </Label>
                  <Input
                    data-testid="cadastre-surface"
                    type="number"
                    placeholder="500"
                    value={criteria.surface_min}
                    onChange={(e) => setCriteria({ ...criteria, surface_min: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                  />
                </div>

                {/* Type de parcelle */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-slate-300">
                    <Layers className="h-4 w-4 text-[#00B050]" />
                    Type de parcelle
                  </Label>
                  <Select 
                    value={criteria.type_parcelle}
                    onValueChange={(value) => setCriteria({ ...criteria, type_parcelle: value })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tous">Tous types</SelectItem>
                      <SelectItem value="terrain">Terrain nu</SelectItem>
                      <SelectItem value="bati">Bâti</SelectItem>
                      <SelectItem value="agricole">Agricole</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  data-testid="cadastre-submit"
                  className="w-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] hover:opacity-90 mt-4"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Lancer la recherche IA
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {/* Loading State */}
            {loading && (
              <Card className="shadow-lg border-0">
                <CardContent className="py-10">
                  <div className="max-w-md mx-auto">
                    {/* Animated Bot Icon */}
                    <div className="text-center mb-8">
                      <div className="relative mx-auto w-24 h-24 mb-4">
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] opacity-20 animate-ping"></div>
                        <div className="absolute inset-2 rounded-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] opacity-30 animate-pulse"></div>
                        <div className="absolute inset-4 rounded-full bg-white flex items-center justify-center shadow-lg">
                          <Map className="h-10 w-10 text-[#0D47A1] animate-bounce" />
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-[#0D47A1] mb-1">Agent IA en action</h3>
                      <p className="text-slate-500 text-sm">Analyse approfondie en cours...</p>
                    </div>

                    {/* Progress Steps */}
                    <div className="mb-8">
                      <div className="flex justify-between mb-3">
                        {loadingSteps.map((step, index) => (
                          <div key={index} className="flex flex-col items-center flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-1 transition-all duration-500 ${
                              index < loadingStep 
                                ? 'bg-[#00B050] text-white' 
                                : index === loadingStep 
                                  ? 'bg-[#0D47A1] text-white animate-pulse' 
                                  : 'bg-slate-200 text-slate-400'
                            }`}>
                              {index < loadingStep ? (
                                <CheckCircle2 className="h-4 w-4" />
                              ) : (
                                index + 1
                              )}
                            </div>
                            <span className={`text-xs text-center ${
                              index <= loadingStep ? 'text-slate-700 font-medium' : 'text-slate-400'
                            }`}>
                              {step.label}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] transition-all duration-1000 ease-out"
                          style={{ width: `${((loadingStep + 1) / loadingSteps.length) * 100}%` }}
                        ></div>
                      </div>
                      
                      {/* Current Step Description */}
                      <p className="text-center text-sm text-[#0D47A1] mt-3 font-medium animate-pulse">
                        {loadingSteps[loadingStep]?.description}
                      </p>
                    </div>

                    {/* Estimated Time */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Temps estimé : 15-30 secondes</p>
                          <p className="text-xs text-amber-600">L'IA analyse les données cadastrales pour vous</p>
                        </div>
                      </div>
                    </div>

                    {/* Rotating Tips */}
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-sm text-slate-600 transition-opacity duration-500">
                        {loadingMessage}
                      </p>
                    </div>

                    {/* What's happening */}
                    <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                      <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <Search className="h-5 w-5 text-[#0D47A1] mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Scan parcelles</p>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <FileText className="h-5 w-5 text-[#00B050] mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Analyse données</p>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <Target className="h-5 w-5 text-amber-500 mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Génération carte</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results */}
            {results && !loading && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <Card className="border-0 shadow-md">
                    <CardContent className="pt-4 text-center">
                      <p className="text-3xl font-bold text-[#0D47A1]">{results.parcelles?.total?.toLocaleString()}</p>
                      <p className="text-sm text-slate-500">Parcelles</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md">
                    <CardContent className="pt-4 text-center">
                      <p className="text-3xl font-bold text-[#00B050]">{results.batiments?.total?.toLocaleString()}</p>
                      <p className="text-sm text-slate-500">Bâtiments</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md">
                    <CardContent className="pt-4 text-center">
                      <p className="text-3xl font-bold text-amber-600">{results.sections?.total?.toLocaleString()}</p>
                      <p className="text-sm text-slate-500">Sections</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md">
                    <CardContent className="pt-4 text-center">
                      <p className="text-3xl font-bold text-purple-600">{results.commune?.nom}</p>
                      <p className="text-sm text-slate-500">Commune</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Layer Toggles */}
                <Card className="border-0 shadow-md">
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Couches visibles :</span>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={visibleLayers.parcelles}
                            onChange={(e) => setVisibleLayers({...visibleLayers, parcelles: e.target.checked})}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm text-[#0D47A1] font-medium">Parcelles</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={visibleLayers.batiments}
                            onChange={(e) => setVisibleLayers({...visibleLayers, batiments: e.target.checked})}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm text-[#00B050] font-medium">Bâtiments</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={visibleLayers.sections}
                            onChange={(e) => setVisibleLayers({...visibleLayers, sections: e.target.checked})}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm text-amber-600 font-medium">Sections</span>
                        </label>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* AI Analysis */}
                {results.analyse && (
                  <Card className="border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Bot className="h-5 w-5 text-[#0D47A1]" />
                        Analyse IA
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="prose prose-sm max-w-none">
                        <p className="text-slate-700 whitespace-pre-line">{results.analyse.summary}</p>
                      </div>
                      {results.analyse.insights && (
                        <div className="mt-4 grid grid-cols-2 gap-3">
                          {results.analyse.insights.map((insight, idx) => (
                            <div key={idx} className="flex items-start gap-2 bg-slate-50 p-3 rounded-lg">
                              <Info className="h-4 w-4 text-[#0D47A1] mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-slate-600">{insight}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Map - Pappers Immobilier */}
                <Card className="border-0 shadow-lg overflow-hidden">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Map className="h-5 w-5 text-[#00B050]" />
                        Carte Pappers Immobilier
                      </div>
                      <a 
                        href={`https://immobilier.pappers.fr/?lat=${selectedCommune?.coordinates?.[1] || 46.45}&lon=${selectedCommune?.coordinates?.[0] || 2.35}&z=17.00`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Ouvrir en plein écran
                      </a>
                    </CardTitle>
                    <p className="text-xs text-slate-500">Parcelles • DVF • Propriétaires • DPE • PLU • Permis</p>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="h-[600px] w-full">
                      <iframe
                        src={`https://immobilier.pappers.fr/?lat=${selectedCommune?.coordinates?.[1] || 46.45}&lon=${selectedCommune?.coordinates?.[0] || 2.35}&z=17.00`}
                        className="w-full h-full border-0"
                        title="Pappers Immobilier"
                        allow="geolocation"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Empty State */}
            {!loading && !results && (
              <Card className="shadow-lg border-0">
                <CardContent className="py-16 text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00B050]/20 to-[#0D47A1]/20 flex items-center justify-center mx-auto mb-4">
                    <Map className="h-10 w-10 text-[#0D47A1]" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Recherchez des parcelles cadastrales</h3>
                  <p className="text-slate-500 max-w-md mx-auto">
                    Entrez une adresse précise (ex: "15 rue de la Paix, Paris") pour découvrir les parcelles cadastrales et obtenir une analyse IA détaillée.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>


      {/* Parcel Detail Modal - Version avec Onglets */}
      <Dialog open={!!selectedParcel} onOpenChange={() => setSelectedParcel(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Square className="h-5 w-5 text-[#0D47A1]" />
                <span>Parcelle {selectedParcel?.properties?.section}{selectedParcel?.properties?.numero}</span>
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {selectedParcel?.properties?.id}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedParcel && (
            <div className="space-y-4">
              {/* En-tête avec infos essentielles */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 text-center">
                  <Ruler className="h-4 w-4 text-blue-600 mx-auto mb-1" />
                  <p className="font-bold text-blue-700">{formatSurface(selectedParcel.properties.contenance)}</p>
                  <p className="text-xs text-blue-500">Surface</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 text-center">
                  <Layers className="h-4 w-4 text-green-600 mx-auto mb-1" />
                  <p className="font-bold text-green-700">{selectedParcel.properties.section || 'N/A'}</p>
                  <p className="text-xs text-green-500">Section</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 text-center">
                  <MapPin className="h-4 w-4 text-purple-600 mx-auto mb-1" />
                  <p className="font-bold text-purple-700 text-sm truncate">{selectedParcel.properties.commune || selectedCommune?.nom}</p>
                  <p className="text-xs text-purple-500">Commune</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 text-center">
                  {loadingBatiment ? (
                    <Loader2 className="h-4 w-4 text-amber-600 mx-auto mb-1 animate-spin" />
                  ) : parcelBatiment?.has_batiment ? (
                    <Home className="h-4 w-4 text-amber-600 mx-auto mb-1" />
                  ) : (
                    <TreePine className="h-4 w-4 text-amber-600 mx-auto mb-1" />
                  )}
                  <p className="font-bold text-amber-700 text-sm">
                    {loadingBatiment ? '...' : parcelBatiment?.has_batiment ? 'Bâti' : 'Terrain'}
                  </p>
                  <p className="text-xs text-amber-500">Type</p>
                </div>
              </div>

              {/* Bouton Pappers Immobilier */}
              {selectedCommune?.coordinates && (
                <Button 
                  variant="outline"
                  size="sm"
                  className="w-full border-purple-400 text-purple-600 hover:bg-purple-50"
                  onClick={() => {
                    const lat = selectedCommune.coordinates[1];
                    const lon = selectedCommune.coordinates[0];
                    window.open(`https://immobilier.pappers.fr/?lat=${lat}&lon=${lon}&z=18.00`, '_blank');
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Voir plus de données sur Pappers Immobilier
                </Button>
              )}

              {/* Onglets pour les différentes sections */}
              <Tabs defaultValue="prix" className="w-full">
                <TabsList className="grid w-full grid-cols-4 h-10">
                  <TabsTrigger value="prix" className="text-xs gap-1">
                    <Euro className="h-3 w-3" />
                    Prix & DVF
                  </TabsTrigger>
                  <TabsTrigger value="urbanisme" className="text-xs gap-1">
                    <Landmark className="h-3 w-3" />
                    Urbanisme
                  </TabsTrigger>
                  <TabsTrigger value="risques" className="text-xs gap-1">
                    <ShieldAlert className="h-3 w-3" />
                    Risques
                  </TabsTrigger>
                  <TabsTrigger value="infos" className="text-xs gap-1">
                    <Info className="h-3 w-3" />
                    Détails
                  </TabsTrigger>
                </TabsList>

                {/* Onglet Prix & DVF */}
                <TabsContent value="prix" className="mt-4 space-y-4">
                  {/* Estimation */}
                  {loadingEstimation ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400 mr-2" />
                      <span className="text-slate-500">Calcul de l'estimation...</span>
                    </div>
                  ) : parcelEstimation?.estimation_disponible ? (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-slate-600">Estimation basée sur {parcelEstimation.nombre_transactions} transactions</span>
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Estimation basse</p>
                          <p className="font-bold text-lg text-slate-700">{formatPrice(parcelEstimation.estimation?.basse)}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm border-2 border-green-300">
                          <p className="text-xs text-green-600 font-medium mb-1">Estimation moyenne</p>
                          <p className="font-bold text-2xl text-green-600">{formatPrice(parcelEstimation.estimation?.moyenne)}</p>
                          <p className="text-xs text-slate-500 mt-1">{parcelEstimation.prix_m2?.median?.toLocaleString()} €/m²</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Estimation haute</p>
                          <p className="font-bold text-lg text-slate-700">{formatPrice(parcelEstimation.estimation?.haute)}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-sm text-slate-500">
                      Pas assez de données pour estimer le prix
                    </div>
                  )}

                  {/* Transactions récentes */}
                  <div>
                    <h4 className="font-medium text-sm text-slate-700 mb-2">Transactions récentes du secteur</h4>
                    {loadingTransactions ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      </div>
                    ) : parcelTransactions.length > 0 ? (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {parcelTransactions.map((tx, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                            <div>
                              <p className="font-medium">{tx.type_bien}</p>
                              <p className="text-xs text-slate-500">{tx.surface} m² • {tx.date}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">{formatPrice(tx.valeur)}</p>
                              <p className="text-xs text-slate-500">{tx.prix_m2?.toLocaleString()} €/m²</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-2">Aucune transaction trouvée</p>
                    )}
                  </div>
                </TabsContent>

                {/* Onglet Urbanisme */}
                <TabsContent value="urbanisme" className="mt-4">
                  {loadingUrbanisme ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400 mr-2" />
                      <span className="text-slate-500">Récupération des données PLU...</span>
                    </div>
                  ) : parcelUrbanisme?.zone_plu ? (
                    <div className="space-y-4">
                      <div className={`rounded-lg p-5 ${
                        parcelUrbanisme.constructible === true 
                          ? 'bg-gradient-to-br from-green-50 to-green-100 border border-green-200' 
                          : parcelUrbanisme.constructible === 'sous_conditions'
                            ? 'bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200'
                            : parcelUrbanisme.constructible === false
                              ? 'bg-gradient-to-br from-red-50 to-red-100 border border-red-200'
                              : 'bg-slate-50 border border-slate-200'
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="text-3xl font-bold text-slate-800">{parcelUrbanisme.zone_plu}</span>
                            {parcelUrbanisme.constructible === true && (
                              <Badge className="bg-green-600 hover:bg-green-600">
                                <Check className="h-3 w-3 mr-1" /> Constructible
                              </Badge>
                            )}
                            {parcelUrbanisme.constructible === 'sous_conditions' && (
                              <Badge className="bg-amber-600 hover:bg-amber-600">
                                <HelpCircle className="h-3 w-3 mr-1" /> Sous conditions
                              </Badge>
                            )}
                            {parcelUrbanisme.constructible === false && (
                              <Badge className="bg-red-600 hover:bg-red-600">
                                <Ban className="h-3 w-3 mr-1" /> Non constructible
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-slate-700 font-medium">{parcelUrbanisme.type_description}</p>
                        {parcelUrbanisme.libelle_zone && (
                          <p className="text-sm text-slate-500 mt-1">{parcelUrbanisme.libelle_zone}</p>
                        )}
                      </div>
                      <a
                        href="https://www.geoportail-urbanisme.gouv.fr"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Consulter le règlement complet sur geoportail-urbanisme.gouv.fr
                      </a>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Landmark className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Données PLU non disponibles pour cette parcelle</p>
                      <p className="text-xs text-slate-400 mt-1">Consultez la mairie pour plus d'informations</p>
                    </div>
                  )}
                </TabsContent>

                {/* Onglet Risques */}
                <TabsContent value="risques" className="mt-4">
                  {loadingRisques ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400 mr-2" />
                      <span className="text-slate-500">Analyse des risques...</span>
                    </div>
                  ) : parcelRisques ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        {/* Zone Inondable */}
                        <div className={`rounded-lg p-4 ${
                          parcelRisques.inondation?.present 
                            ? 'bg-blue-50 border-2 border-blue-300' 
                            : 'bg-green-50 border border-green-200'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Droplets className={`h-5 w-5 ${parcelRisques.inondation?.present ? 'text-blue-600' : 'text-green-600'}`} />
                            <span className="font-semibold text-sm">Zone inondable</span>
                          </div>
                          {parcelRisques.inondation?.present ? (
                            <div className="flex items-center gap-1 text-blue-700">
                              <AlertTriangle className="h-4 w-4" />
                              <span className="text-sm font-medium">Risque identifié</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-green-700">
                              <ShieldCheck className="h-4 w-4" />
                              <span className="text-sm">Aucun risque</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Sites Pollués */}
                        <div className={`rounded-lg p-4 ${
                          parcelRisques.pollution?.present 
                            ? 'bg-orange-50 border-2 border-orange-300' 
                            : 'bg-green-50 border border-green-200'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Factory className={`h-5 w-5 ${parcelRisques.pollution?.present ? 'text-orange-600' : 'text-green-600'}`} />
                            <span className="font-semibold text-sm">Sites pollués</span>
                          </div>
                          {parcelRisques.pollution?.present ? (
                            <div className="text-orange-700">
                              <div className="flex items-center gap-1">
                                <AlertTriangle className="h-4 w-4" />
                                <span className="text-sm font-medium">{parcelRisques.pollution.nombre_sites} site(s)</span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-green-700">
                              <ShieldCheck className="h-4 w-4" />
                              <span className="text-sm">Aucun site</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Autres risques */}
                      {parcelRisques.risques_naturels?.filter(r => r.libelle).length > 0 && (
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-slate-600 mb-2">Autres risques identifiés :</p>
                          <div className="flex flex-wrap gap-1">
                            {parcelRisques.risques_naturels.filter(r => r.libelle).slice(0, 6).map((risque, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {risque.libelle}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <a
                        href="https://www.georisques.gouv.fr/mes-risques/connaitre-les-risques-pres-de-chez-moi"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Consulter georisques.gouv.fr
                      </a>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <ShieldAlert className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Données de risques non disponibles</p>
                    </div>
                  )}
                </TabsContent>

                {/* Onglet Détails */}
                <TabsContent value="infos" className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Identifiant complet</p>
                      <p className="font-mono text-sm font-bold text-slate-700">{selectedParcel.properties.id}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Code INSEE</p>
                      <p className="font-mono text-sm font-bold text-slate-700">{selectedParcel.properties.commune || selectedCommune?.code}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Date de création</p>
                      <p className="text-sm font-medium">{selectedParcel.properties.created || 'N/A'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Dernière modification</p>
                      <p className="text-sm font-medium">{selectedParcel.properties.updated || 'N/A'}</p>
                    </div>
                  </div>
                  
                  {/* Liens externes */}
                  <div className="flex gap-2 pt-2">
                    <a
                      href={`https://www.cadastre.gouv.fr/scpc/rechercherPlan.do?action=selectCommune&commune=${selectedParcel.properties.commune || selectedCommune?.code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button variant="outline" size="sm" className="w-full gap-2 text-xs">
                        <ExternalLink className="h-3 w-3" />
                        cadastre.gouv.fr
                      </Button>
                    </a>
                    <a
                      href={`https://app.dvf.etalab.gouv.fr/?code_insee=${selectedParcel.properties.commune || selectedCommune?.code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button variant="outline" size="sm" className="w-full gap-2 text-xs">
                        <ExternalLink className="h-3 w-3" />
                        DVF Etalab
                      </Button>
                    </a>
                    <a
                      href="https://www.geoportail-urbanisme.gouv.fr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button variant="outline" size="sm" className="w-full gap-2 text-xs">
                        <ExternalLink className="h-3 w-3" />
                        Urbanisme
                      </Button>
                    </a>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CadastreAgentPage;
